import { z } from 'zod';
import { PaginationSchema } from '@visep/shared-types/common';
import type { Pagination } from '@visep/shared-types/common';
import {
  CreateGuardiaRequestSchema,
  CreateHorasRequestSchema,
  GuardiaOptionSchema,
  GuardiaSchema,
  GuardiasPdfQuerySchema,
  GuardiasPorSucursalResponseSchema,
  HorasTrabajadaSchema,
  PersonalDocumentSchema,
  UpdateGuardiaRequestSchema,
  UpdateHorasRequestSchema,
} from '@visep/shared-types/hr';
import type {
  GuardiaOption,
  GuardiasPorSucursalResponse,
  HorasTrabajada,
  PersonalDocument,
} from '@visep/shared-types/hr';
import type { Helpers, ListResult } from './internal.js';
import { listResultSchema, makeCrudResource, toQuery } from './internal.js';

export type HorasListQuery = Pagination & { guardiaId?: string; sucursalId?: string; from?: string; to?: string };

export function createGuardiasResource({ request, requestMultipart, requestBlob }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/guardias',
    schema: GuardiaSchema,
    listQuerySchema: PaginationSchema,
    createSchema: CreateGuardiaRequestSchema,
    updateSchema: UpdateGuardiaRequestSchema,
  });
  return {
    ...crud,
    // Shape enxuto p/ dropdowns/agrupamento (sem salário mensal nem PII).
    async options(query?: Pagination): Promise<ListResult<GuardiaOption>> {
      const parsed = query ? PaginationSchema.parse(query) : undefined;
      return listResultSchema(GuardiaOptionSchema).parse(
        await request('GET', `/api/guardias/options${parsed ? toQuery(parsed) : ''}`),
      );
    },
    // Agregação server-side (fix/096): resposta única, sem fan-out paginado.
    async porSucursal(): Promise<GuardiasPorSucursalResponse> {
      return GuardiasPorSucursalResponseSchema.parse(
        await request('GET', '/api/guardias/por-sucursal'),
      );
    },
    async exportPdfBySucursal(sucursalId: string): Promise<Blob> {
      const parsed = GuardiasPdfQuerySchema.parse({ sucursalId });
      return requestBlob(`/api/guardias/pdf${toQuery(parsed)}`);
    },
    async exportPdfByCliente(clienteId: string): Promise<Blob> {
      return requestBlob(`/api/guardias/cliente/${encodeURIComponent(clienteId)}/pdf`);
    },
    async uploadAvatar(id: string, file: Blob | File): Promise<{ avatarUrl: string }> {
      const form = new FormData();
      // filename obrigatório p/ @fastify/multipart reconhecer como arquivo (Blob puro
      // sem nome → missing_file 422). Ver uploads.image.
      form.append('file', file, file instanceof File ? file.name : 'upload');
      return (await requestMultipart(`/api/guardias/${encodeURIComponent(id)}/avatar`, form)) as {
        avatarUrl: string;
      };
    },
    async listDocuments(guardiaId: string): Promise<PersonalDocument[]> {
      const data = await request('GET', `/api/guardias/${encodeURIComponent(guardiaId)}/documents`);
      return z.object({ items: z.array(PersonalDocumentSchema) }).parse(data).items;
    },
    async uploadDocument(guardiaId: string, file: Blob | File): Promise<PersonalDocument> {
      const form = new FormData();
      form.append('file', file, file instanceof File ? file.name : 'document');
      return PersonalDocumentSchema.parse(
        await requestMultipart(`/api/guardias/${encodeURIComponent(guardiaId)}/documents`, form),
      );
    },
    async downloadDocument(guardiaId: string, docId: string): Promise<Blob> {
      return requestBlob(
        `/api/guardias/${encodeURIComponent(guardiaId)}/documents/${encodeURIComponent(docId)}/download`,
      );
    },
    async deleteDocument(guardiaId: string, docId: string): Promise<void> {
      await request(
        'DELETE',
        `/api/guardias/${encodeURIComponent(guardiaId)}/documents/${encodeURIComponent(docId)}`,
      );
    },
  };
}

export function createHorasResource({ request, requestBlob }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/horas',
    schema: HorasTrabajadaSchema,
    listQuerySchema: PaginationSchema,
    createSchema: CreateHorasRequestSchema,
    updateSchema: UpdateHorasRequestSchema,
  });
  return {
    ...crud,
    async exportPdfByGuardia(guardiaId: string): Promise<Blob> {
      return requestBlob(`/api/guardias/${encodeURIComponent(guardiaId)}/horas/pdf`);
    },
    async list(query?: HorasListQuery): Promise<ListResult<HorasTrabajada>> {
      const parsed = PaginationSchema.parse(query ?? {});
      const merged = {
        ...parsed,
        guardiaId: query?.guardiaId,
        sucursalId: query?.sucursalId,
        from: query?.from,
        to: query?.to,
      };
      return listResultSchema(HorasTrabajadaSchema).parse(
        await request('GET', `/api/horas${toQuery(merged)}`),
      );
    },
    async listByGuardia(
      guardiaId: string,
      query?: Pagination,
    ): Promise<ListResult<HorasTrabajada>> {
      const parsed = PaginationSchema.parse(query ?? {});
      return listResultSchema(HorasTrabajadaSchema).parse(
        await request('GET', `/api/guardias/${encodeURIComponent(guardiaId)}/horas${toQuery(parsed)}`),
      );
    },
  };
}
